import React, {RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {getObjectByPath} from "../../utils";
import {ExchangeRatesContext, ExchangeRatesContextValue} from '../../contexts/exchange-rates-context';
import {FieldTableColumnProps, FieldTableProps, IdedType} from "./FieldTableTypes";
import {get} from 'immutadot';
import {FieldTableSummaryRow} from './FieldTableSummaryRow';
import {FieldRow} from './FieldRow';
import {FieldTableSettingsContext, FieldTableSettingsContextValue} from '../../contexts/field-table-settings-context';
import {Button, styled} from "@mui/material";
import {AddBox, Download} from "@mui/icons-material";
import WaitSpinner from "../wait-spinner";
import {useDaybookContext} from "../../contexts/Daybook/DayBookContext";
import { TableThemeProvider } from '../../theme';
import { useFieldTableExportRaw } from './FieldTableExport';

function regexReplace(s) {
    return s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
}

type TableSubErrors = {
    [key:string]:any
}
type TableErrors = {
    [key:string]:TableSubErrors
};


type FieldTableColumnHeaderProps<Type,PropsType> = {
    column: FieldTableColumnProps<Type,PropsType>,
    doSort: ()=>void,
    toggleOpenAll: ()=>void,
    toggleSelectAll: ()=>void,
    selectedAll: boolean,
    openedAll: boolean,
    sortState: any,
    loading: boolean
};




const renderColumnHeader = (columnTypesContext, column, doSort, exchangeRateContext, toggleOpenAll, toggleSelectAll, selectedAll, openedAll, sortState, loading) => {

    if (column.exportOnly) return null;
    if (column.hideWhen) {
        if (column.hideWhen({exchangeRateContext: exchangeRateContext})) {
            return null;
        }
    }
//    columnCount++;
    const sortAccessor = column.sort ? column.sort : column.accessor;
    let className = '';
    sortState.forEach((val) => {
        if (val.name === sortAccessor) {
            className = val.asc ? ' sort-up' : ' sort-down';
        }
    });
    const columnDef =(column.dataType && columnTypesContext.register[column.dataType])?columnTypesContext.register[column.dataType]:columnTypesContext.register['default'];
        let header = null;
        if (columnDef.renderHeader) {
            header = columnDef.renderHeader({
                column,
                toggleOpenAll,
                toggleSelectAll,
                selectedAll,
                openedAll,
                sortState,
                loading
            });
        } else {
            header = column.header;
        }
    return (
        <FieldTableHeaderCell key={'header-' + column.accessor}
                              className={'field-table__header-cell ' + [columnDef.className(), column.className, className].join(' ')}
                              onClick={columnDef.noSort | column.noSort ? null : doSort(sortAccessor)}>{header}</FieldTableHeaderCell>);
};

const FieldTableHeaderCell = styled('div', {
    name: 'FieldTable', slot: 'header',
    shouldForwardProp: (prop) =>
        prop !== 'error' && prop !== 'color' && prop !== 'variant' && prop !== 'sx',
    overridesResolver: (props, styles) => [
        styles.header
    ],
})({});

const FieldTableRootNode = styled('div',
    {
        name: 'FieldTable', slot: 'root',
        shouldForwardProp: (prop) =>
            prop !== 'colour' && prop !== 'variant' && prop !== 'sx',
        overridesResolver: (props, styles) => [
            styles.root
        ]
    })({});

const FieldTableFooter = styled('div',
    {
        name: 'FieldTable', slot: 'footer',
        shouldForwardProp: (prop) =>
            prop !== 'colour' && prop !== 'variant' && prop !== 'sx',
        overridesResolver: (props, styles) => [
            styles.footer
        ]
    })({});

const FieldTableNoData = styled('div',
    {
        name: 'FieldTable', slot: 'noData',
        shouldForwardProp: (prop) =>
            prop !== 'colour' && prop !== 'variant' && prop !== 'sx',
        overridesResolver: (props, styles) => [
            styles.noData
        ]
    })({});
type SortOrder =  {
    name:string,
    asc:boolean
};

export function FieldTable<Type extends IdedType,RowPropsType>(props: FieldTableProps<Type,RowPropsType>) {

    const exchangeRateContext = useContext<ExchangeRatesContextValue|null>(ExchangeRatesContext);
    const [sortState, setSortState] = useState<SortOrder[]>(props.defaultSort || []);
    const [tableErrors, setTableErrors] = useState<TableErrors|null>({});
    const [excludedIds, setExcludedIds] = useState({});
    const [footerHeight, setFooterHeight] = useState(0);
    const { errors, setValue} = props.form ? props.form : props;
    const footerRef = useRef<RefObject<HTMLDivElement>>();
    const columnTypesContext = useContext<FieldTableSettingsContextValue|null>(FieldTableSettingsContext);
    const noResults = false;

    // tablevalues is
    let tableValues:Type[]|null = null;
    if (props.loading) {
        tableValues = [];
    } else {
        if(props.values || !props.form) {
            tableValues = props.values;
        } else {
            tableValues = getObjectByPath(props.form?.values, props.name);
        }
    }
    console.log('tablevalues are',tableValues);
    useEffect(() => {
        setFooterHeight(footerRef?.current?.clientHeight);
    }, [footerRef]);

    useEffect(() => {
        if(!tableValues) return;
        if (errors) {
            const myRe = new RegExp(regexReplace(props.name) + '\\[(\\d+)\\]\\.(.*)');
            const te:TableErrors = {};
            Object.entries(errors).forEach(([k, v]) => {
                const match = myRe.exec(k);
                if (match) {
                    // we recieve the index, but we want to record the ID of the row...
                    const matchRow = tableValues?tableValues[parseInt(match[1])]:null;
                    if(!matchRow) return;
                    const id = matchRow?.id;
                    if (te[id]) {
                        te[id][match[2]] = v;
                    } else {
                        te[id] = {[match[2]]: v};
                    }
                }
            });
            setTableErrors(te);
        } else {
            setTableErrors(null);
        }
    }, [errors]);
    const [openRows, setOpenRows] = useState({});
    const [sortedOrder, setSortedOrder] = useState([]);
    const [selectedAll, setSelectedAll] = useState(false);
    const [openedAll, setOpenedAll] = useState(false);
    const [focusedRow, setFocusedRow] = useState({});
    const config = useDaybookContext();
    console.log('daybook fieldtable',config);
    const onChangeRowFocus = useCallback((rowIdx) => {
        setFocusedRow(rowIdx);
    }, [setFocusedRow]);
    const addRow = useCallback((evt) => {
        if (evt) {
            evt.preventDefault();
        }
        if (props.addRowGuard) {
            if (!props.addRowGuard()) return;
        }
        if(!setValue) return;
        setValue(props.name??'', (values) => {
            console.log('Diaform', 'pushing', Object.assign({id: Math.floor(Date.now()) + '-' + Math.floor(Math.random() * 1000000)}, typeof props.newRow == 'function' ? props.newRow() : props.newRow), 'onto', values);
            const retVal = values.concat([Object.assign({id: Math.floor(Date.now()) + '-' + Math.floor(Math.random() * 1000000)}, typeof props.newRow == 'function' ? props.newRow() : props.newRow)]);
            //console.log('Diaform', 'pushed', props.newRow(), 'onto', [...values]);
            return retVal;
            //[...values];
        });
    }, [props.name, props.newRow, tableValues?.length, props.addRowGuard]);
    const toggleOpen = useCallback((id) => {
        setOpenRows((rows) => {
            rows[id] = !rows[id];
            return Object.assign({}, rows);
        });
    }, []);

    const excludeId = useCallback((id) => {
        setExcludedIds((rows) => {
            rows[id] = !rows[id];
            return Object.assign({}, rows);
        });
    }, []);

    const toggleOpenAll = useCallback(() => {
        if(!tableValues) return;
        const changeTo = !openedAll;
        const newState = {};
        tableValues.forEach(function (val) {
            newState[val.id] = changeTo;
        });
        setOpenedAll(changeTo);
        setOpenRows(newState);
    }, [setOpenedAll, openedAll, setOpenRows, tableValues]);

    const {exportData} = useFieldTableExportRaw({
        tableValues: tableValues,
        columns:props.exportColumns?props.exportColumns:props.columns,
        columnTypesContext:columnTypesContext,
        exchangeRateContext:exchangeRateContext,
        getRowProps:props.getRowProps,
        excludedIds:excludedIds,
        name:props.name??'',
        config:config
    });
    const toggleSelected = useCallback((id) => {
        if(!props.setSelectedRows) return;
        props.setSelectedRows((rows) => {
            const newRows = {...rows};
            newRows[id] = !newRows[id];
            return newRows;
        });
    }, [props.setSelectedRows]);
    const toggleSelectAll = useCallback(() => {
        const changeTo = !selectedAll;
        setSelectedAll(changeTo);
        if(!props.setSelectedRows) return;
        props.setSelectedRows((selected) => {
            const newSel = {};
            Object.entries(selected).forEach(([k, v]) => {
                newSel[k] = !v;
            });
            return newSel;
        });
    }, [selectedAll, props.setSelectedRows]);

    const onChange = useCallback((evt) => {
        if(!setValue) return;
        setValue(evt.target.name, evt.target.value);
    }, []);

    const deleteRowById = useCallback((evt) => {
        //        tableValues.splice(idx,1);
        // tableValues is considered an identical array, so clone it...
        const id = evt.target.closest('.field-table__row').dataset.id;
        if (props.doDelete) {
            props.doDelete(id);
            return;
        }
        if (setValue) {
            setValue(props.name, (vals) => {
                const idx = vals.findIndex((el) => el.id == id);
                if (idx <= -1) {
                    return;
                }
                vals.splice(idx, 1);
                return [...vals];
            });
        }
    }, [props.name]);

    const doSort = useCallback((column) => (evt) => {
        let newSortState:SortOrder[] = [];
        if (!evt.shiftKey) {
            // without shift, set sort absolutely for any column
            const asc = (sortState.length && sortState[0].name === column) ? !sortState[0].asc : true;
            newSortState = [{name: column, asc: asc}];
        } else {
            let isDone = false;
            sortState.forEach((val) => {
                if (val.name === column) {
                    val.asc = !val.asc;
                    isDone = true;
                }
                newSortState.push(val);
            });
            if (!isDone) {
                newSortState.push({name: column, asc: true});
            }
        }
        setSortState(newSortState);
    }, [sortState]);


    useEffect(() => {
        if(!tableValues) return;
        let so:(Type|number)[] = [];
        if (props.loading || noResults) {
            for (let i = 0; i < tableValues.length; i++) {
                so.push(i);
            }
        } else {
            // tableValues may be out of date, but we don't entirely care...
            if (sortState.length) {
                const sortValues = tableValues.map((e, i) => ({ind: i, val: e}));
                sortValues.sort(
                    (x, y) => {
                        let fieldA = get(x.val, sortState[0].name);
                        let fieldB = get(y.val, sortState[0].name);
                        let ret = 0;
                        if (sortState[0].asc) {
                            ret = fieldA > fieldB ? 1 : (fieldA === fieldB ? 0 : -1);
                        } else {
                            ret = fieldA < fieldB ? 1 : (fieldA === fieldB ? 0 : -1);
                        }
                        if (!ret && sortState.length > 1) {
                            fieldA = get(x.val, sortState[1].name);
                            fieldB = get(y.val, sortState[1].name);
                            if (sortState[1].asc) {
                                ret = fieldA > fieldB ? 1 : (fieldA === fieldB ? 0 : -1);
                            } else {
                                ret = fieldA < fieldB ? 1 : (fieldA === fieldB ? 0 : -1);
                            }
                        }
                        return ret;
                    }
                );
                so = sortValues.map((e) => (e.ind));
            } else {
                so = (tableValues ? Array.from(tableValues.keys()) : []);
            }
        }
        setSortedOrder(so);
        if (props.setSelectedRows) {
            props.setSelectedRows((selected) => {
                const newSel = {};
                if(tableValues && selected) {
                    tableValues.forEach((tableValue) => {
                        newSel[tableValue.id] = selected[tableValue.id] ?? false;
                    });
                }
                return newSel;
            });
        }
    }, [sortState, tableValues?.length, props.loading]);
    console.log('FieldTable', 'render?', props, tableValues, tableErrors, sortedOrder,config);
    let columnCount = 0;
    const styles = props.style ?? {};
    return useMemo(() => {
        console.log('FieldTable','memo render');
        //        console.log('[FTR]','Change variables are',[focusedRow, onChangeRowFocus, openRows, tableValues, tableErrors, sortState, props.selectedRows]);
        let header:JSX.Element|null = null;
        styles.gridTemplateColumns = props.columns.map((column) => {
            if (column.exportOnly) return;
            if (column.hideWhen) {
                if (column.hideWhen({exchangeRateContext: exchangeRateContext})) {
                    return;
                }
            }
            if (column.width) {
                switch (column.width) {
                    case 'full':
                        return '10fr';
                    case 'narrow':
                    case 'buttons':
                        return 'min-content';
                    default:
                        return column.width;
                }
            }
            if (column.dataType && columnTypesContext?.register[column.dataType]) {
                return columnTypesContext?.register[column.dataType].width() ?? 'auto';
            } else {
                return columnTypesContext?.register['default'].width() ?? 'auto';

            }
            //switch (true) {
                /*case column.excluder:
                    return '24px';
                case column.expander:
                    return '24px';
                case column.selector:
                    return '24px';*/
                //default:
            switch (column.width) {
                case 'full':
                    return '10fr';
                case 'narrow':
                case 'buttons':
                    return 'min-content';
            }
                    //return 'auto';
            //}
        }).join(' ');

        if (props.hasDelete) {
            styles.gridTemplateColumns += ' 28px';
        }
        if (props.showHeadings) {
            const headings:JSX.Element[] = [];
            props.columns.forEach((column) => {
                const p = renderColumnHeader(columnTypesContext, column, doSort, exchangeRateContext, toggleOpenAll, toggleSelectAll, selectedAll, openedAll, sortState, props.loading);
                if (p) headings.push(p);
            });
            if (props.hasDelete) {
                headings.push(<FieldTableHeaderCell key="header-delete"
                                   className="field-table__header-cell column-button column-delete"></FieldTableHeaderCell>);
            }
            columnCount = headings.length;
            header = <div className="field-table__header-row">{headings}</div>;
//            header = <div className="field-table__header-row">{headings}</div>;
        } else {
            props.columns.map((column) => {
                if (!column.exportOnly) columnCount++;
            });
            if (props.hasDelete) {
                columnCount++;
            }
        }
        // fields.value contains the value, use that to sortState...

        const rows:JSX.Element[] = [];
        const summaryRows = {};
        let oldRowData:Type|null = null;
        let rowData:Type|null = null;
        if (props.summaries) {
            Object.keys(props.summaries).forEach((name) => {
                summaryRows[name] = [];
            })
        }
        sortedOrder.forEach(index => {
            if(!tableValues) return;
            const name = props.name + '[' + index + ']';
            let rowProps:RowPropsType|undefined = undefined;
            rowData = tableValues[index];
            if (!rowData) {
                return [];
            }
            if (excludedIds[rowData.id]) return [];
            if (props.getRowProps) {
                rowProps = props.getRowProps({data: rowData, calcExchangeRate: exchangeRateContext?.calcExchangeRate,config:config});
            }
            if (!props.loading && !noResults && props.summaries) {
                Object.entries(props.summaries).forEach(([name, test]) => {
                    if (test(oldRowData, rowData)) {
                        rows.push(<FieldTableSummaryRow exchangeRateContext={exchangeRateContext} name={name}
                                                        readOnly={props.readOnly} columns={props.columns}
                                                        rows={summaryRows[name]} hasDelete={props.hasDelete}/>);
                        summaryRows[name] = [];
                    } else {
                        summaryRows[name].push(rowData);
                    }
                });
            }
            oldRowData = rowData;
            rows.push(<FieldRow columnTypesContext={columnTypesContext} columns={props.columns}
                                deleteRowById={deleteRowById}
                                key={rowData.id}
                                name={name}
                                readOnly={!!props.readOnly}
                                onChangeRowFocus={onChangeRowFocus}
                                rowData={rowData}
                                exchangeRate={exchangeRateContext}
                                getRowClass={props.getRowClass}
                                rowErrors={tableErrors && tableErrors[rowData.id]}
                                isRowOpen={openRows[rowData.id]}
                                toggleOpen={toggleOpen}
                                excludeId={excludeId}
                                rowClass={props.getRowClass}
                                toggleSelected={toggleSelected}
                                isSelected={props.selectedRows ? !!props.selectedRows[rowData.id] : false}
                                onChange={onChange}
                                setValue={setValue}
                                isEditingRow={focusedRow === rowData.id}
                                hasDelete={props.hasDelete}
                                rowProps={rowProps}
                                loading={props.loading}
            >{props.children}</FieldRow>);
        });
        if (!props.loading && props.summaries && tableValues?.length) {
            Object.entries(props.summaries).forEach(([name, test]) => {
                if (test(rowData, null)) {
                    if (name == 'header') {
                        //preHeader = <FieldTableSummaryRow readOnly={props.readOnly} currency={currency} name={name} key={'summary-row-' + name} columns={props.columns} summary={name} rows={summaryRows[name]} hasDelete={props.hasDelete} />;
                        rows.unshift(<FieldTableSummaryRow readOnly={props.readOnly}
                                                           exchangeRateContext={exchangeRateContext} name={name}
                                                           key={'summary-row-' + name} columns={props.columns}
                                                           summary={name} rows={summaryRows[name]}
                                                           hasDelete={props.hasDelete}/>);
                    } else {
                        rows.push(<FieldTableSummaryRow readOnly={props.readOnly}
                                                        exchangeRateContext={exchangeRateContext} name={name}
                                                        key={'summary-row-' + name} columns={props.columns}
                                                        summary={name} rows={summaryRows[name]}
                                                        hasDelete={props.hasDelete}/>);
                    }
                    summaryRows[name] = [];
                } else {
                    summaryRows[name].push(rowData);
                }
            });
        }

        const footer = [];
        if (props.footer) {
            footer.push(props.footer);
        }
        const footerBtns = [];
        if (props.hasExport) {
            footerBtns.push(<Button variant="outlined" color="info" key="exportBtn" size="large" onClick={()=>exportData(tableValues)} startIcon={<Download/>}>Export</Button>);
        }
        if (!props.readOnly && props.newRowLabel) {
            footerBtns.push(<Button variant="contained" color="primary" key="addBtn" size="large" onClick={addRow} startIcon={<AddBox/>}>{props.newRowLabel}</Button>);
        }
        if (footerBtns.length) {
            footer.push(<div key="defaultBtns" className="field-table__footer__btns">{footerBtns}</div>)
        }
        if(props.loading) {
            rows.push(<WaitSpinner variant="table"/>);
        }
        if(!tableValues || !tableValues.length) {
            rows.push(
                <FieldTableNoData><Button variant="contained" disabled={true}>No data</Button></FieldTableNoData>)
        }
        styles["--footer-height"] = footerHeight + 'px';
        return <TableThemeProvider><FieldTableRootNode
            className={'field-table table-columns-' + columnCount + (props.children ? ' field-table-has-children' : ' field-table-no-children') + (props.className ? ' ' + props.className : '') + (props.isScrollable ? ' is-scrollable' : '')}
            style={styles}>
            {props.headerButtons ? props.headerButtons : []}
            {header}
            {rows}
            {footer.length>0 &&
              <React.Fragment key="footer">
                <FieldTableFooter ref={footerRef}
                                  className={'field-table__footer field-table__span ' + (props.stickyFooter ? ' sticky' : '') + (props.footerClassName ? ' ' + props.footerClassName : '')}
                                  key="footerB">{
                    footer
                }</FieldTableFooter>
              </React.Fragment>
            }
        </FieldTableRootNode></TableThemeProvider>;
    }, [focusedRow, config,onChangeRowFocus, props.readOnly, openRows, tableValues, tableErrors, sortedOrder, props.selectedRows, props.loading, exchangeRateContext?.targetCurrency, exchangeRateContext?.options, exchangeRateContext?.calcExchangeRate, excludedIds, props.columns]);
}


FieldTable.defaultProps = {
    showHeadings: true,
    hasDelete: true,
    errors: null,
    hasExport: true,
    noRowsLabel: 'No rows found',
    noRowsLength: 5
};

